import {
  ClearOutlined,
  CloseOutlined,
  CompressOutlined,
  FullscreenExitOutlined,
  FullscreenOutlined,
  MoreOutlined,
  ReloadOutlined,
  VerticalLeftOutlined,
  VerticalRightOutlined,
} from '@ant-design/icons';
import {
  closestCenter,
  DndContext,
  type DragEndEvent,
  type Modifier,
} from '@dnd-kit/core';
import { horizontalListSortingStrategy, SortableContext } from '@dnd-kit/sortable';
import {
  Button,
  ConfigProvider,
  Dropdown,
  Flex,
  type MenuProps,
  Tabs,
  theme,
} from 'antd';
import {
  cloneElement,
  type HTMLAttributes,
  type ReactElement,
  type RefObject,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useLocation } from 'react-router-dom';
import { useGuardedNavigation } from '@/app/NavigationFeedback';
import { PressRipple } from '@/components/PressRipple';
import { routeLabel } from '@/config/routeLabels';
import { useShellText } from '@/hooks/useShellText';
import { useLocaleStore } from '@/stores/localeStore';
import { type CloseTabsAction, tabsAfterClose, useTabStore } from '@/stores/tabStore';
import SortableTab from './SortableTab';
import { useAppLayoutStyles } from './styles';
import { useTabDragSensors } from './tab-drag/useTabDragSensors';

const restrictToHorizontalAxis: Modifier = ({ transform }) => ({ ...transform, y: 0 });

type MultiTabsProps = {
  isMobile: boolean;
  isReloading?: boolean;
  onReload: () => void;
  workspaceRef: RefObject<HTMLDivElement | null>;
};

export default function MultiTabs({
  isMobile,
  isReloading = false,
  onReload,
  workspaceRef,
}: MultiTabsProps) {
  const { styles } = useAppLayoutStyles();
  const { token } = theme.useToken();
  const guard = useGuardedNavigation();
  const location = useLocation();
  const locale = useLocaleStore((state) => state.locale);
  const t = useShellText();
  const tabs = useTabStore((state) => state.tabs);
  const activeTabId = useTabStore((state) => state.activeTabId);
  const activateTab = useTabStore((state) => state.activateTab);
  const closeTabs = useTabStore((state) => state.closeTabs);
  const reorderTabs = useTabStore((state) => state.reorderTabs);
  const [fullscreen, setFullscreen] = useState(false);
  useEffect(() => {
    if (isMobile) setFullscreen(false);
  }, [isMobile]);
  const tabsHeight = token.controlHeight + token.marginXS;
  const { sensors, canReorder } = useTabDragSensors(isMobile, token.screenLG);
  const sortableIds = useMemo(
    () => (canReorder ? tabs.filter((tab) => tab.closable).map((tab) => tab.id) : []),
    [canReorder, tabs],
  );
  const popupContainer = () => workspaceRef.current ?? document.body;

  useEffect(() => {
    const workspace = workspaceRef.current;
    if (!workspace) return;
    if (fullscreen && !isMobile)
      workspace.setAttribute('data-admin-shell-fullscreen', 'true');
    else workspace.removeAttribute('data-admin-shell-fullscreen');
    return () => workspace.removeAttribute('data-admin-shell-fullscreen');
  }, [fullscreen, isMobile, workspaceRef]);

  function reload(id: string) {
    if (id === activeTabId && !isReloading) {
      void guard(() => onReload());
    }
  }
  function close(action: CloseTabsAction, id: string) {
    if (tabsAfterClose(tabs, action, id).some((tab) => tab.id === activeTabId)) {
      closeTabs(action, id);
      return;
    }
    void guard((navigate) => {
      const path = closeTabs(action, id);
      if (path !== `${location.pathname}${location.search}${location.hash}`) {
        navigate(path);
      }
    });
  }
  function menuFor(id: string): MenuProps {
    const index = tabs.findIndex((tab) => tab.id === id);
    const target = tabs[index];
    const items: MenuProps['items'] = [
      {
        key: 'reload',
        label: t('重新加载', 'Reload'),
        icon: <ReloadOutlined aria-hidden />,
        disabled: id !== activeTabId || isReloading,
      },
      {
        key: 'current',
        label: t('关闭当前标签页', 'Close current tab'),
        icon: <CloseOutlined aria-hidden />,
        disabled: !target?.closable,
      },
      { type: 'divider' },
      {
        key: 'left',
        label: t('关闭左侧标签页', 'Close tabs to the left'),
        icon: <VerticalLeftOutlined aria-hidden />,
        disabled: !tabs.slice(0, index).some((tab) => tab.closable),
      },
      {
        key: 'right',
        label: t('关闭右侧标签页', 'Close tabs to the right'),
        icon: <VerticalRightOutlined aria-hidden />,
        disabled: !tabs.slice(index + 1).some((tab) => tab.closable),
      },
      { type: 'divider' },
      {
        key: 'others',
        label: t('关闭其它标签页', 'Close other tabs'),
        icon: <CompressOutlined aria-hidden />,
        disabled: !tabs.some((tab) => tab.closable && tab.id !== id),
      },
      {
        key: 'all',
        label: t('关闭全部标签页', 'Close all tabs'),
        icon: <ClearOutlined aria-hidden />,
        disabled: !tabs.some((tab) => tab.closable),
      },
    ];
    return {
      items,
      onClick: ({ key }) => {
        if (key === 'reload') reload(id);
        else if (['current', 'left', 'right', 'others', 'all'].includes(key))
          close(key as CloseTabsAction, id);
      },
    };
  }
  function handleChange(id: string) {
    const tab = tabs.find((item) => item.id === id);
    if (!tab) return;
    if (id !== activeTabId) {
      void guard((navigate) => {
        activateTab(id);
        navigate(tab.path);
      });
    }
  }
  function handleDragEnd({ active, over }: DragEndEvent) {
    if (canReorder && over) reorderTabs(String(active.id), String(over.id));
  }
  const title = (id: string | number) => {
    const tab = tabs.find((item) => item.id === String(id));
    return tab ? routeLabel(tab.path, tab.label, locale) : '';
  };

  return (
    <ConfigProvider theme={{ token: { borderRadius: 8 } }}>
      <div className={styles.tabsWrap} data-testid="tabs-bar">
        <PressRipple targetSelector="button.ant-tabs-nav-more">
          <Tabs
            activeKey={activeTabId}
            aria-label={t('页面标签', 'Page tabs')}
            hideAdd
            type="editable-card"
            items={tabs.map((tab) => ({
              key: tab.id,
              label: <span>{routeLabel(tab.path, tab.label, locale)}</span>,
              closable: tab.closable,
            }))}
            onChange={handleChange}
            onEdit={(key, action) => {
              if (action === 'remove' && typeof key === 'string') close('current', key);
            }}
            getPopupContainer={popupContainer}
            style={{
              background: token.colorBgContainer,
              boxSizing: 'border-box',
              flex: `0 0 ${tabsHeight}px`,
              height: tabsHeight,
              minHeight: tabsHeight,
              padding: 0,
              width: '100%',
            }}
            styles={{
              header: {
                margin: 0,
                height: tabsHeight,
                minHeight: tabsHeight,
              },
              item: { height: tabsHeight, paddingBlock: 0 },
            }}
            tabBarExtraContent={
              <ConfigProvider
                theme={{
                  components: {
                    Button: {
                      colorFill: 'transparent',
                      textHoverBg: 'var(--tab-action-hover)',
                    },
                  },
                }}
              >
                <PressRipple targetSelector="button[data-press-ripple-target]">
                  <Flex
                    align="center"
                    gap={0}
                    style={{
                      height: tabsHeight,
                      boxSizing: 'border-box',
                      paddingBottom: token.lineWidth,
                      paddingInlineStart: token.marginXS,
                    }}
                  >
                    {!isMobile && (
                      <Button
                        data-press-ripple-target
                        className={styles.tabAction}
                        aria-label={t('重新加载', 'Reload')}
                        disabled={isReloading}
                        icon={<ReloadOutlined aria-hidden spin={isReloading} />}
                        onClick={() => reload(activeTabId)}
                        type="text"
                      />
                    )}
                    {!isMobile && (
                      <Button
                        data-press-ripple-target
                        className={styles.tabAction}
                        aria-label={
                          fullscreen
                            ? t('退出全屏', 'Exit fullscreen')
                            : t('全屏', 'Fullscreen')
                        }
                        aria-pressed={fullscreen}
                        icon={
                          fullscreen ? (
                            <FullscreenExitOutlined aria-hidden />
                          ) : (
                            <FullscreenOutlined aria-hidden />
                          )
                        }
                        onClick={() => {
                          if (workspaceRef.current) setFullscreen((value) => !value);
                        }}
                        type="text"
                      />
                    )}
                    <Dropdown
                      getPopupContainer={popupContainer}
                      menu={menuFor(activeTabId)}
                      popupRender={(menu) => <PressRipple>{menu}</PressRipple>}
                      trigger={['click']}
                    >
                      <Button
                        data-press-ripple-target
                        className={styles.tabAction}
                        aria-label={t('更多标签操作', 'More tab actions')}
                        icon={<MoreOutlined aria-hidden />}
                        type="text"
                      />
                    </Dropdown>
                  </Flex>
                </PressRipple>
              </ConfigProvider>
            }
            more={{
              trigger: isMobile ? 'click' : 'hover',
              popupRender: (menu) => (
                <PressRipple targetSelector='[role="option"]'>{menu}</PressRipple>
              ),
            }}
            renderTabBar={(tabBarProps, DefaultTabBar) => (
              <DndContext
                key={canReorder ? 'sortable' : 'scroll-only'}
                sensors={sensors}
                collisionDetection={closestCenter}
                modifiers={[restrictToHorizontalAxis]}
                onDragEnd={handleDragEnd}
                accessibility={{
                  // 原生AntD标签管理焦点；取消后不能把用户已移出的焦点强拉回来。
                  restoreFocus: false,
                  screenReaderInstructions: {
                    draggable: t(
                      '按空格键拾取标签，使用左右方向键调整位置，再次按空格键放下，或按 Esc 键取消。',
                      'Press Space to pick up a tab, Left or Right to move, Space to drop, or Escape to cancel.',
                    ),
                  },
                  announcements: {
                    onDragStart: ({ active }) =>
                      t('已拾取{title}。', 'Picked up {title}.', {
                        title: title(active.id),
                      }),
                    onDragOver: ({ active, over }) =>
                      over
                        ? t(
                            '正在将{title}移动到{target}的位置。',
                            'Moving {title} to {target}.',
                            { title: title(active.id), target: title(over.id) },
                          )
                        : undefined,
                    onDragEnd: ({ active, over }) =>
                      over
                        ? t(
                            '已将{title}移动到{target}的位置。',
                            'Moved {title} to {target}.',
                            { title: title(active.id), target: title(over.id) },
                          )
                        : undefined,
                    onDragCancel: ({ active }) =>
                      t('已取消拖拽{title}。', 'Cancelled dragging {title}.', {
                        title: title(active.id),
                      }),
                  },
                }}
              >
                <SortableContext
                  items={sortableIds}
                  strategy={horizontalListSortingStrategy}
                >
                  <DefaultTabBar
                    {...tabBarProps}
                    // Compatibility adapter: rc-tabs hides overflow on mobile. Keep
                    // this override until AntD exposes a public opt-in; covered by mobile overflow tests.
                    mobile={false}
                    // rc-tabs device detection also covers wide phone viewports.
                    more={{
                      ...tabBarProps.more,
                      trigger: isMobile || tabBarProps.mobile ? 'click' : 'hover',
                    }}
                  >
                    {(node) => {
                      const tab = tabs.find((item) => item.id === String(node.key));
                      const element = node as ReactElement<
                        HTMLAttributes<HTMLDivElement>
                      >;
                      if (!canReorder || !tab?.closable)
                        return cloneElement(element, {
                          style: { ...element.props.style, cursor: 'default' },
                        });
                      return (
                        <SortableTab
                          key={node.key}
                          node={element}
                          menu={menuFor(tab.id)}
                          getPopupContainer={popupContainer}
                          className={styles.sortableTab}
                          draggingClassName={styles.draggingTab}
                        />
                      );
                    }}
                  </DefaultTabBar>
                </SortableContext>
              </DndContext>
            )}
          />
        </PressRipple>
      </div>
    </ConfigProvider>
  );
}
